import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button, Callout, Field, inputClass } from '@/components/ui';

/**
 * The first recipe for a product and method. R-162.
 *
 * `POST /api/recipes` was written to close the gap where nothing could create
 * version 1 (R-152), and it was never given a screen: the route existed for a
 * day with no way to reach it. The route-callers guard did not see it, because
 * the recipe LIST reads the same path and the guard matched paths without
 * methods.
 *
 * Deliberately small. One product, one method, one line, because a recipe with
 * no lines makes a job look costed when it is not, and the second line is
 * `Supersede` on the row this creates.
 */
export function NewRecipe({
  products,
  methods,
  items,
}: {
  products: { id: string; name: string }[];
  methods: { key: string; name: string }[];
  items: { id: string; code: string; name: string; base_uom: string }[];
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [productId, setProductId] = useState('');
  const [methodKey, setMethodKey] = useState('');
  const [itemId, setItemId] = useState('');
  const [qty, setQty] = useState('');
  const [error, setError] = useState<string | null>(null);

  const uom = items.find((i) => i.id === itemId)?.base_uom ?? '';

  const create = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/recipes', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          product_id: productId,
          method_key: methodKey,
          lines: [{ item_id: itemId, qty_per_unit: Number(qty), uom }],
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { error?: { message?: string } } | null;
        // The server says which move is right when one already exists, so its
        // words are shown rather than a generic failure.
        throw new Error(body?.error?.message ?? `Request failed (${res.status})`);
      }
      return res.json();
    },
    onSuccess: () => {
      setError(null);
      setOpen(false);
      setProductId('');
      setMethodKey('');
      setItemId('');
      setQty('');
      void qc.invalidateQueries({ queryKey: ['admin', 'recipes'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  if (!open) {
    return (
      <div className="mb-5">
        <Button onClick={() => setOpen(true)}>New recipe</Button>
      </div>
    );
  }

  const ready = productId && methodKey && itemId && Number(qty) > 0;

  return (
    <div className="mb-5 rounded-[10px] border border-line bg-panel p-5">
      <h2 className="text-lg font-bold tracking-[-0.02em]">New recipe</h2>
      <p className="mt-1 text-[14px] text-muted">
        What one unit consumes. Add the rest of the lines by superseding it.
      </p>

      {error && (
        <div className="mt-4">
          <Callout tone="danger" title={error} />
        </div>
      )}

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <Field label="Product" htmlFor="recipe-product">
          <select
            id="recipe-product"
            className={inputClass}
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
          >
            <option value="">Choose a product</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </Field>

        <Field label="Decoration" htmlFor="recipe-method">
          <select
            id="recipe-method"
            className={inputClass}
            value={methodKey}
            onChange={(e) => setMethodKey(e.target.value)}
          >
            <option value="">Choose a method</option>
            {methods.map((m) => (
              <option key={m.key} value={m.key}>{m.name}</option>
            ))}
          </select>
        </Field>

        <Field label="Material" htmlFor="recipe-item">
          <select
            id="recipe-item"
            className={inputClass}
            value={itemId}
            onChange={(e) => setItemId(e.target.value)}
          >
            <option value="">Choose a material</option>
            {items.map((i) => (
              <option key={i.id} value={i.id}>{i.code} {i.name}</option>
            ))}
          </select>
        </Field>

        <Field label={`Quantity per unit${uom ? ` (${uom})` : ''}`} htmlFor="recipe-qty">
          <input
            id="recipe-qty"
            className={inputClass}
            inputMode="decimal"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
          />
        </Field>
      </div>

      <div className="mt-5 flex gap-3">
        <Button onClick={() => create.mutate()} disabled={!ready || create.isPending}>
          {create.isPending ? 'Saving...' : 'Create recipe'}
        </Button>
        <Button weight="secondary" onClick={() => setOpen(false)}>Cancel</Button>
      </div>
    </div>
  );
}
